import axios from 'axios';
import SeriesSvc from '@/services/SeriesSvc';
import { getIntervalSeconds } from '@/util/ship/fms-summary';
import { browserUtcOffsetHours } from '@/util/apiDatetime';
import {
  seriesToSummaryData, buildChunkRanges, rowsPerBucket, maxBucketsFor,
  SERIES_REQUEST_PARAMS,
} from '@/util/summary/seriesToSummaryData';

const TYPES = ['gps', 'rpm', 'flowmeter', 'ae', 'temperature'];

// Narik deret waktu buat chart dari /series/aggregated, lalu dibentuk balik ke
// bentuk `summary.{tipe}.data` punya endpoint lama supaya buildFmsSeries dan
// komponen chart gak perlu diubah.
//
// Endpoint ini punya batas KERAS 6000 BARIS dan motong diam-diam kalau kena
// (terverifikasi: 90 hari flowmeter 3 device @HOUR = 6.480 baris diharapkan,
// yang balik cuma 6.000, tanpa error). Baris = bucket x channel, jadi rentang
// panjang dipecah per tipe berdasarkan anggaran baris.
//
// Potongan dijalanin BERURUTAN, bukan paralel. Chunking paralel pernah bikin
// 93 request barengan dan 29x HTTP 504 di halaman voyage sampai backend minta
// FE menghapusnya (memo 2026-07-18). Yang paralel cuma antar-tipe.
//
// `rpmTypes`/`aeCount` WAJIB dari body rollup yang baru di-fetch, bukan dari
// deviceStatus: deviceStatus ngisi dua field itu dari data pulse, yang cuma
// ngeliat payload terakhir — device yang lagi offline ilang dari hitungan,
// potongan jadi 2x kegedean, dan limit 6000 kena diam-diam lagi.
export async function fetchSummarySeriesData({
  devices = [], start, end, interval, rpmTypes = [], aeCount = 0, tempPositions = [],
  requestParams = SERIES_REQUEST_PARAMS,
  // Halaman yang cuma baca satu tipe gak perlu narik lima. Bunker Dashboard
  // cuma butuh flowmeter, dan dia dipakai di interval minute — gps/rpm di
  // minute kena chunking berat buat data yang gak kebaca sama sekali.
  seriesTypes,
}) {
  const type = (seriesTypes && seriesTypes.length)
    ? TYPES.filter((t) => seriesTypes.includes(t))
    : TYPES;
  const uidsOf = (type) => devices
    .filter((d) => d.devcType === type)
    .map((d) => d.devcUniqueId);

  const rows = rowsPerBucket({
    gpsCount: uidsOf('gps').length,
    flowmeterCount: uidsOf('flowmeter').length,
    rpmTypes,
    aeCount,
    tempPositions,
  });

  const intervalSeconds = getIntervalSeconds(interval) || 3600;

  // Ditiru dari store/asset.js fetchSummary — jalur legacy kirim aggregatedUnit
  // doang, tanpa aggregatedLength. Kalau beda, angka chart gak sepadan.
  const base = {
    aggregatedUnit: String(interval).toUpperCase(),
    timezone: browserUtcOffsetHours(),
  };

  const failed = [];

  const fetchType = async (type) => {
    const device = uidsOf(type);
    if (!device.length) return [];

    const chunks = buildChunkRanges(start, end, maxBucketsFor(rows[type]) * intervalSeconds);
    const out = [];

    for (let i = 0; i < chunks.length; i += 1) {
      try {
        // Sengaja berurutan — lihat catatan 504 di atas.
        // eslint-disable-next-line no-await-in-loop
        const res = await SeriesSvc.getAggregatedDataByPeriod(
          type,
          chunks[i].start,
          chunks[i].end,
          { params: { device, ...base, ...requestParams[type] } },
        );
        const body = res?.data?.data || {};
        out.push(...(body.series || []));
        if (body.limit && body.count >= body.limit) {
          // Data separuh lebih menyesatkan daripada chart kosong: chart yang
          // cuma memuat sebagian rentang tetap kelihatan wajar. Buang semuanya.
          console.warn(`[rollup chart] ${type}: satu potongan masih kena limit ${body.limit} — perhitungan chunk meleset, data kemungkinan terpotong`);
          failed.push(type);
          return [];
        }
      } catch (e) {
        // Navigasi keluar mid-fetch (cancelPendingRequests) itu aksi biasa,
        // bukan kegagalan — jangan warn, jangan masuk toast.
        if (!axios.isCancel(e)) {
          console.warn(`[rollup chart] ${type}: potongan ${i + 1}/${chunks.length} gagal, chart tipe ini dikosongkan`, e);
          failed.push(type);
        }
        return [];
      }
    }
    return out;
  };

  // Antar-tipe boleh paralel — satu request barengan per tipe, lihat TYPES.
  // Yang dilarang itu potongan-potongannya yang paralel.
  const result = await Promise.all(type.map(fetchType));

  const labelByUid = {};
  devices
    .filter((d) => d.devcType === 'flowmeter' && d.devcLabel)
    .forEach((d) => { labelByUid[d.devcUniqueId] = d.devcLabel; });

  // Grup suhu nempel di device, bukan di baris: devcLabel-nya persis nama grup
  // yang dipakai endpoint lama (TEMPERATURE / EXHAUST_TEMPERATURE).
  const tempGroupByUid = {};
  devices
    .filter((d) => d.devcType === 'temperature' && d.devcLabel)
    .forEach((d) => { tempGroupByUid[d.devcUniqueId] = d.devcLabel; });

  const lookup = { flowmeter: labelByUid, temperature: tempGroupByUid };
  const data = {};
  type.forEach((type, i) => {
    data[type] = seriesToSummaryData(type, result[i], lookup[type]);
  });

  return {
    data,
    // Urutannya distabilin biar pesan toast-nya konsisten.
    failedTypes: type.filter((t) => failed.includes(t)),
  };
}

export default fetchSummarySeriesData;
